/**
 * House of Bon — Bundle Configurator
 * Vanilla JS IIFE — window.HOBConfigurator
 * No dependencies.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Helper utilities
  ───────────────────────────────────────────── */

  /**
   * Format cents as a price string (e.g. 4500 → "45,00 €")
   * Reads locale from <html lang>; falls back to fr-FR / EUR.
   */
  function formatMoney(cents) {
    var lang = document.documentElement.lang || 'fr-FR';
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'EUR';
    try {
      return new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  /**
   * Safe querySelector with scope
   */
  function qs(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function qsa(sel, scope) {
    return Array.from((scope || document).querySelectorAll(sel));
  }

  /* ─────────────────────────────────────────────
     Accordion — section-level init
  ───────────────────────────────────────────── */
  function initAccordions(root) {
    qsa('.cfg-acc__head', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var row = btn.closest('.cfg-acc__row');
        if (!row) return;
        var isOpen = row.classList.contains('is-open');
        // Close sibling accordions in same section
        qsa('.cfg-acc__row.is-open', root).forEach(function (r) {
          r.classList.remove('is-open');
          var h = r.querySelector('.cfg-acc__head');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          row.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     Gallery
  ───────────────────────────────────────────── */
  function initGallery(data, root) {
    var sid = data.sectionId;
    var mainImg = qs('#cfg-gal-main-img-' + sid, root);
    var counter = qs('#cfg-gal-counter-' + sid, root);
    var prevBtn = qs('#cfg-gal-prev-' + sid, root);
    var nextBtn = qs('#cfg-gal-next-' + sid, root);
    var thumbBtns = qsa('.cfg-gal__thumb', root);

    if (!mainImg || !data.galleryImages || data.galleryImages.length === 0) return;

    var images = data.galleryImages; // [{src, srcset, alt, w, h}]
    var currentIdx = 0;

    function goTo(idx) {
      if (idx < 0) idx = images.length - 1;
      if (idx >= images.length) idx = 0;
      currentIdx = idx;

      var img = images[idx];
      mainImg.src = img.src;
      if (img.srcset) mainImg.srcset = img.srcset;
      mainImg.alt = img.alt || '';

      if (counter) counter.textContent = (idx + 1) + ' / ' + images.length;

      thumbBtns.forEach(function (tb, i) {
        tb.classList.toggle('is-active', i === idx);
      });
    }

    thumbBtns.forEach(function (tb) {
      tb.addEventListener('click', function () {
        var idx = parseInt(tb.getAttribute('data-idx'), 10);
        goTo(idx);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIdx - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIdx + 1); });

    // Swipe support on touch
    var startX = null;
    mainImg.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
    }, { passive: true });
    mainImg.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) {
        dx < 0 ? goTo(currentIdx + 1) : goTo(currentIdx - 1);
      }
      startX = null;
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────
     Variant badges (discount %)
  ───────────────────────────────────────────── */
  function initVariantBadges(data, root) {
    var sid = data.sectionId;
    var variants = data.variants;
    var refUnitPrice = data.bundleRefPrice;
    if (!variants || !refUnitPrice) return;

    variants.forEach(function (v, idx) {
      var refTotal = refUnitPrice * v.bundleSize;
      var saving = 1 - v.price / refTotal;
      if (saving <= 0.001) return;
      var pct = Math.round(saving * 100);
      var btn = qs('[data-variant-idx="' + idx + '"]', root);
      if (!btn) return;
      if (qs('.cfg-variant__badge', btn)) return;
      var badge = document.createElement('span');
      badge.className = 'cfg-variant__badge';
      badge.textContent = 'Save ' + pct + '%';
      btn.appendChild(badge);
    });
  }

  /* ─────────────────────────────────────────────
     Main configurator logic
  ───────────────────────────────────────────── */
  function initConfigurator(data) {
    var sid = data.sectionId;
    var root = qs('[data-section-id="' + sid + '"]');
    if (!root) return;

    /* State */
    var state = {
      bundleSize: data.variants && data.variants[0] ? (data.variants[0].bundleSize || 10) : 10,
      selectedVariantIdx: 0,
      quantities: {}, // productId (string) → qty (int)
      purchaseMode: 'once', // 'once' | 'sub'
      sellingPlanId: null
    };

    // Init quantities map
    if (data.products) {
      data.products.forEach(function (p) {
        state.quantities[String(p.id)] = 0;
      });
    }

    /* Cache DOM refs */
    var atcBtn    = qs('#cfg-atc-' + sid, root);
    var atcTxt    = qs('#cfg-atc-txt-' + sid, root);
    var currEl    = qs('#cfg-curr-' + sid, root);
    var totalEl   = qs('#cfg-total-' + sid, root);
    var needEl    = qs('#cfg-prog-need-' + sid, root);
    var hintEl    = qs('#cfg-prog-hint-' + sid, root);
    var fillEl    = qs('#cfg-prog-fill-' + sid, root);
    var segsEl    = qs('#cfg-prog-segs-' + sid, root);
    var flavGrid  = qs('#cfg-flav-' + sid, root);
    var step2Num  = qs('#cfg-step2-num-' + sid, root);
    var purchaseEl= qs('#cfg-purchase-' + sid, root);
    var priceOnceEl = qs('#cfg-price-once-' + sid, root);
    var priceSubEl  = qs('#cfg-price-sub-' + sid, root);
    var subBadgeEl  = qs('#cfg-sub-badge-' + sid, root);


    // Read Appstle selling plans lazily (window.RS.Config is set by inline script)
    function appstlePlans() {
      return (window.RS && window.RS.Config && window.RS.Config.sellingPlansJson) || [];
    }

    // product.selling_plan_groups is the reliable per-product check (from Liquid).
    // window.RS.Config.sellingPlansJson is store-level and always populated.
    function appstleAvailable() {
      return data.hasSellingPlans === true && appstlePlans().length > 0;
    }

    // If Appstle is not installed/configured, hide the One-time / Subscribe toggle
    // (subscription is meaningless without a selling plan)
    if (purchaseEl && !appstleAvailable()) {
      purchaseEl.style.display = 'none';
      // Force purchase mode to 'once' so handleATC doesn't try to add selling_plan
      state.purchaseMode = 'once';
    }

    function resolveSellingPlanId() {
      // Liquid-sourced: always scoped to the current product, guaranteed valid for its variants
      if (data.sellingPlanId) return String(data.sellingPlanId);
      // Fallback: Appstle global config (store-wide, may not match this product's variant)
      var plans = appstlePlans();
      if (plans.length) {
        return (plans[0].id || '').split('/').pop() || null;
      }
      var manual = purchaseEl ? purchaseEl.getAttribute('data-selling-plan') : null;
      return manual || null;
    }

    function resolveDiscountPct() {
      var plans = appstlePlans();
      if (plans.length && plans[0].discountOffer) {
        return parseInt(plans[0].discountOffer, 10) || 0;
      }
      return parseInt(purchaseEl ? purchaseEl.getAttribute('data-discount') : '10', 10) || 10;
    }

    /* ── Total selected qty ── */
    function totalSelected() {
      return Object.values(state.quantities).reduce(function (s, q) { return s + q; }, 0);
    }

    /* ── Update progress bar / segments ── */
    function updateProgress() {
      var curr = totalSelected();
      var total = state.bundleSize;
      var done = curr >= total;

      if (currEl) currEl.textContent = curr;
      if (totalEl) totalEl.textContent = total;

      // Bar style
      if (fillEl) {
        var pct = Math.min(100, total > 0 ? (curr / total * 100) : 0);
        fillEl.style.width = pct + '%';
        fillEl.classList.toggle('cfg-prog__fill--done', done);
      }

      // Segments style
      if (segsEl) {
        var segs = qsa('.cfg-prog__seg', segsEl);
        // Rebuild if count changed
        if (segs.length !== total) {
          segsEl.innerHTML = '';
          for (var i = 0; i < total; i++) {
            var seg = document.createElement('div');
            seg.className = 'cfg-prog__seg';
            segsEl.appendChild(seg);
          }
          segs = qsa('.cfg-prog__seg', segsEl);
        }
        segs.forEach(function (seg, i) {
          seg.classList.toggle('is-filled', i < curr);
          seg.classList.toggle('is-done', done);
        });
      }

      // Hint text
      if (hintEl && needEl) {
        if (done) {
          hintEl.classList.add('cfg-prog__hint--done');
          hintEl.innerHTML = '<span>' + (data.i18n.progressComplete || 'Bundle complete') + '</span>';
        } else {
          hintEl.classList.remove('cfg-prog__hint--done');
          var need = state.bundleSize - total;
          var unitSingular = data.bundleUnit || 'item';
          var unitPlural = data.bundleUnits || (unitSingular + 's');
          var hintTpl = need === 1 ? (data.i18n.progressHintOne || 'Pick 1 more') : (data.i18n.progressHintOther || 'Pick {count} more');
          var unitForTpl = need === 1 ? unitSingular : unitPlural;
          hintEl.innerHTML = '<span>' + hintTpl.replace('{count}', need).replace('{unit}', unitForTpl).replace('{units}', unitPlural) + '</span>';
        }
      }

      // Step 2 indicator
      if (step2Num) {
        step2Num.classList.toggle('cfg-step__num--done', done);
      }
    }

    /* ── Update flavour grid (corner badges, active state, button states) ── */
    function updateFlavGrid() {
      var curr = totalSelected();
      var full = curr >= state.bundleSize;

      if (!flavGrid) return;

      qsa('.cfg-flav__cell:not(.cfg-flav__cell--mkt)', flavGrid).forEach(function (cell) {
        var pid = cell.getAttribute('data-product-id');
        if (!pid) return;
        var qty = state.quantities[pid] || 0;
        var isActive = qty > 0;

        cell.classList.toggle('is-active', isActive);

        // Corner badge

        // Qty display
        var qtyEl = qs('[data-qty-for="' + pid + '"]', cell);
        if (qtyEl) {
          var valEl = qs('.cfg-qty__val', qtyEl);
          if (valEl) valEl.textContent = qty;
          qtyEl.classList.toggle('cfg-qty--zero', qty === 0);

          var decBtn = qs('[data-qty-dec="' + pid + '"]', cell);
          var incBtn = qs('[data-qty-inc="' + pid + '"]', cell);
          if (decBtn) decBtn.disabled = qty === 0;
          if (incBtn) incBtn.disabled = full && qty === 0; // can't add if full and zero
        }
      });
    }

    /* ── Update purchase toggle prices ── */
    function updatePrices() {
      if (!data.variants || !data.variants[state.selectedVariantIdx]) return;
      var v = data.variants[state.selectedVariantIdx];
      var price = v.price;
      if (priceOnceEl) priceOnceEl.textContent = formatMoney(price);

      if (purchaseEl) {
        var discountPct = resolveDiscountPct();
        var subPrice = Math.round(price * (1 - discountPct / 100));
        if (priceSubEl) priceSubEl.textContent = formatMoney(subPrice);
        if (subBadgeEl) subBadgeEl.textContent = '−' + discountPct + '%';
      }
    }

    /* ── Update ATC button ── */
    function updateATC() {
      if (!atcBtn) return;
      var done = totalSelected() >= state.bundleSize;
      atcBtn.disabled = !done;

      if (atcTxt) {
        if (done) {
          var v = data.variants && data.variants[state.selectedVariantIdx];
          var price = v ? v.price : 0;
          if (state.purchaseMode === 'sub' && purchaseEl) {
            var discountPct = resolveDiscountPct();
            price = Math.round(price * (1 - discountPct / 100));
          }
          atcTxt.innerHTML = (data.i18n.atcAdd || 'Add to cart') + ' &nbsp;<span class="cfg-atc__sep"></span><span class="cfg-atc__price">' + formatMoney(price) + '</span>';
        } else {
          var remaining = state.bundleSize - totalSelected();
          var unitSingular = data.bundleUnit || 'item';
          var unitPlural = data.bundleUnits || (unitSingular + 's');
          var pickTpl = remaining === 1 ? (data.i18n.atcPickMoreOne || 'Pick 1 more {unit}') : (data.i18n.atcPickMoreOther || 'Pick {count} more {units}');
          atcTxt.textContent = pickTpl.replace('{count}', remaining).replace('{unit}', unitSingular).replace('{units}', unitPlural);
        }
      }
    }

    /* ── Master update ── */
    function updateAll() {
      updateProgress();
      updateFlavGrid();
      updatePrices();
      updateATC();
    }

    /* ── Variant selection ── */
    qsa('.cfg-variant', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        qsa('.cfg-variant', root).forEach(function (b) { b.classList.remove('is-selected'); });
        btn.classList.add('is-selected');

        var idx = parseInt(btn.getAttribute('data-variant-idx'), 10);
        state.selectedVariantIdx = idx;

        var newSize = parseInt(btn.getAttribute('data-bundle-size') || '10', 10);
        state.bundleSize = newSize;

        // Trim quantities if over new size
        var curr = totalSelected();
        if (curr > newSize) {
          var excess = curr - newSize;
          var pids = Object.keys(state.quantities).reverse();
          for (var i = 0; i < pids.length && excess > 0; i++) {
            var pid = pids[i];
            var remove = Math.min(state.quantities[pid], excess);
            state.quantities[pid] -= remove;
            excess -= remove;
          }
        }

        // Update step 1 indicator
        var step1Num = qs('#cfg-step1-num-' + sid, root);
        if (step1Num) step1Num.classList.add('cfg-step__num--done');

        updateAll();
      });
    });

    /* ── Quantity buttons (event delegation on root) ── */
    root.addEventListener('click', function (e) {
      // Inc
      var incTarget = e.target.closest('[data-qty-inc]');
      if (incTarget) {
        var pid = incTarget.getAttribute('data-qty-inc');
        incQty(pid);
        return;
      }
      // Dec
      var decTarget = e.target.closest('[data-qty-dec]');
      if (decTarget) {
        var pid2 = decTarget.getAttribute('data-qty-dec');
        decQty(pid2);
        return;
      }
    });

    function incQty(pid) {
      pid = String(pid);
      // Block selection if product is unavailable
      var p = data.products && data.products.find(function (x) { return String(x.id) === pid; });
      if (p && p.available === false) return;
      var curr = totalSelected();
      if (curr >= state.bundleSize) return;
      state.quantities[pid] = (state.quantities[pid] || 0) + 1;
      updateAll();
    }

    function decQty(pid) {
      pid = String(pid);
      if ((state.quantities[pid] || 0) <= 0) return;
      state.quantities[pid] -= 1;
      updateAll();
    }

    /* ── Purchase toggle ── */
    if (purchaseEl) {
      purchaseEl.addEventListener('click', function (e) {
        var opt = e.target.closest('.cfg-purchase__opt');
        if (!opt) return;
        qsa('.cfg-purchase__opt', purchaseEl).forEach(function (o) { o.classList.remove('is-selected'); });
        opt.classList.add('is-selected');
        state.purchaseMode = opt.getAttribute('data-purchase') || 'once';
        updateAll();
      });
    }

    /* ── Subscription promo button ── */
    var subPromoBtn = qs('#cfg-sub-promo-btn-' + sid, root);
    if (subPromoBtn && purchaseEl) {
      subPromoBtn.addEventListener('click', function () {
        var subOpt = qs('.cfg-purchase__opt[data-purchase="sub"]', purchaseEl);
        if (subOpt) subOpt.click();
        subPromoBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    /* ── Modal ── */
    function showModal(productId) {
      productId = String(productId);
      var templateEl = qs('#hob-cfg-modal-content-' + sid + '-' + productId);
      if (!templateEl) return;

      // Find product data
      var product = null;
      if (data.products) {
        for (var i = 0; i < data.products.length; i++) {
          if (String(data.products[i].id) === productId) {
            product = data.products[i];
            break;
          }
        }
      }

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Build modal DOM
      var modal = document.createElement('div');
      modal.className = 'hob-cfg-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', product ? product.title : 'Product detail');

      var backdrop = document.createElement('div');
      backdrop.className = 'hob-cfg-modal__backdrop';
      backdrop.addEventListener('click', closeModal);
      modal.appendChild(backdrop);

      var panel = document.createElement('div');
      panel.className = 'hob-cfg-modal__panel';

      // Close button
      var closeBtn = document.createElement('button');
      closeBtn.className = 'hob-cfg-modal__close';
      closeBtn.innerHTML = '&times;';
      closeBtn.setAttribute('aria-label', 'Fermer');
      closeBtn.addEventListener('click', closeModal);
      panel.appendChild(closeBtn);

      // Image column
      var imgCol = document.createElement('div');
      imgCol.className = 'hob-cfg-modal__img-col';

      var imgWrap = document.createElement('div');
      imgWrap.className = 'hob-cfg-modal__img';

      var mainImgEl = document.createElement('img');
      mainImgEl.alt = product ? product.title : '';
      mainImgEl.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';

      var images = (product && product.images && product.images.length > 0)
        ? product.images
        : (product && product.img ? [{ src: product.img }] : []);

      if (images.length > 0) {
        mainImgEl.src = images[0].src || images[0];
      }
      imgWrap.appendChild(mainImgEl);
      imgCol.appendChild(imgWrap);

      // Thumbnails (only if multiple images)
      if (images.length > 1) {
        var thumbsWrap = document.createElement('div');
        thumbsWrap.className = 'hob-cfg-modal__thumbs';

        images.forEach(function (img, idx) {
          var thumbBtn = document.createElement('button');
          thumbBtn.className = 'hob-cfg-modal__thumb' + (idx === 0 ? ' is-active' : '');
          var thumbImg = document.createElement('img');
          thumbImg.src = img.src || img;
          thumbImg.alt = '';
          thumbImg.loading = 'lazy';
          thumbBtn.appendChild(thumbImg);
          thumbBtn.addEventListener('click', function () {
            mainImgEl.src = img.src || img;
            qsa('.hob-cfg-modal__thumb', thumbsWrap).forEach(function (t) { t.classList.remove('is-active'); });
            thumbBtn.classList.add('is-active');
          });
          thumbsWrap.appendChild(thumbBtn);
        });
        imgCol.appendChild(thumbsWrap);
      }

      panel.appendChild(imgCol);

      // Body column
      var body = document.createElement('div');
      body.className = 'hob-cfg-modal__body';

      // Inject pre-rendered content from hidden template
      var templateContent = templateEl.cloneNode(true);
      templateContent.removeAttribute('hidden');
      body.appendChild(templateContent);

      // Init accordions inside modal
      qsa('.cfg-acc__head', body).forEach(function (head) {
        head.addEventListener('click', function () {
          var row = head.closest('.cfg-acc__row');
          if (!row) return;
          var isOpen = row.classList.toggle('is-open');
          head.setAttribute('aria-expanded', String(isOpen));
        });
      });

      // ATC row (in-modal qty + "Add to bundle")
      var atcRow = document.createElement('div');
      atcRow.className = 'hob-cfg-modal__atc';

      var qtyWrapper = document.createElement('div');
      qtyWrapper.className = 'hob-cfg-modal__qty';

      var modalDecBtn = document.createElement('button');
      modalDecBtn.className = 'hob-cfg-modal__qty-btn';
      modalDecBtn.textContent = '−';
      modalDecBtn.setAttribute('aria-label', 'Moins');
      modalDecBtn.disabled = true;

      var modalQtyVal = document.createElement('span');
      modalQtyVal.className = 'hob-cfg-modal__qty-val';
      modalQtyVal.textContent = String(state.quantities[productId] || 0);

      var modalIncBtn = document.createElement('button');
      modalIncBtn.className = 'hob-cfg-modal__qty-btn';
      modalIncBtn.textContent = '+';
      modalIncBtn.setAttribute('aria-label', 'Plus');

      // Sync buttons state
      function syncModalQty() {
        var qty = state.quantities[productId] || 0;
        modalQtyVal.textContent = String(qty);
        modalDecBtn.disabled = qty === 0;
        var full = totalSelected() >= state.bundleSize;
        modalIncBtn.disabled = full && qty === 0;
        addBtn.disabled = full && qty === 0;
        addBtn.textContent = qty > 0
          ? (data.i18n.modalAddAnother || 'Add another')
          : (data.i18n.modalAddToBundle || 'Add to bundle');
      }

      modalDecBtn.addEventListener('click', function () {
        decQty(productId);
        syncModalQty();
      });
      modalIncBtn.addEventListener('click', function () {
        incQty(productId);
        syncModalQty();
      });

      qtyWrapper.appendChild(modalDecBtn);
      qtyWrapper.appendChild(modalQtyVal);
      qtyWrapper.appendChild(modalIncBtn);

      var addBtn = document.createElement('button');
      addBtn.className = 'hob-cfg-modal__atc-btn';
      addBtn.textContent = data.i18n.modalAddToBundle || 'Add to bundle';
      addBtn.disabled = totalSelected() >= state.bundleSize && (state.quantities[productId] || 0) === 0;

      addBtn.addEventListener('click', function () {
        var full = totalSelected() >= state.bundleSize;
        if (!full) {
          incQty(productId);
          syncModalQty();
        } else {
          closeModal();
        }
      });

      atcRow.appendChild(qtyWrapper);
      atcRow.appendChild(addBtn);
      body.appendChild(atcRow);

      panel.appendChild(body);
      modal.appendChild(panel);
      document.body.appendChild(modal);

      // Trap focus
      modal.setAttribute('tabindex', '-1');
      modal.focus();

      // ESC key
      function onKeyDown(e) {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', onKeyDown);
        }
      }
      document.addEventListener('keydown', onKeyDown);

      // Store reference for close
      modal._onKeyDown = onKeyDown;
    }

    function closeModal() {
      var modal = qs('.hob-cfg-modal');
      if (!modal) return;
      if (modal._onKeyDown) document.removeEventListener('keydown', modal._onKeyDown);
      modal.remove();
      document.body.style.overflow = '';
    }

    /* ── Event delegation for modal triggers ── */
    if (flavGrid) {
      flavGrid.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-open-modal]');
        if (!trigger) return;
        var pid = trigger.getAttribute('data-open-modal');
        if (pid) showModal(pid);
      });
      flavGrid.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var trigger = e.target.closest('[role="button"][data-open-modal]');
        if (!trigger) return;
        e.preventDefault();
        var pid = trigger.getAttribute('data-open-modal');
        if (pid) showModal(pid);
      });
    }

    /* ── Build cart composition string ── */
    function buildComposition() {
      var parts = [];
      if (!data.products) return '';
      data.products.forEach(function (p) {
        var qty = state.quantities[String(p.id)] || 0;
        if (qty > 0) {
          parts.push(qty + '× ' + p.title);
        }
      });
      return parts.join(', ');
    }

    /* ── Build SKU selection map ── */
    function buildSkuSelection() {
      var map = {};
      if (!data.products) return map;
      data.products.forEach(function (p) {
        var qty = state.quantities[String(p.id)] || 0;
        if (qty > 0 && p.sku) {
          map[p.sku] = qty;
        }
      });
      return map;
    }

    /* ── Build visual bundle list for cart rendering ── */
    function buildBundleVisual() {
      var list = [];
      if (!data.products) return list;
      data.products.forEach(function (p) {
        var qty = state.quantities[String(p.id)] || 0;
        if (qty > 0) {
          list.push({ title: p.title, img: p.img || null, url: p.url || null, qty: qty });
        }
      });
      return list;
    }

    /* ── handleATC — add to cart ── */
    function handleATC() {
      if (!atcBtn || atcBtn.disabled) return;

      var v = data.variants && data.variants[state.selectedVariantIdx];
      if (!v) return;

      var composition = buildComposition();
      var skuSelection = buildSkuSelection();

      var properties = {
        'Bundle Content': composition,
        '_bundle_content': JSON.stringify(skuSelection),
        '_bundle_visual': JSON.stringify(buildBundleVisual())
      };

      var body = {
        id: v.id,
        quantity: 1,
        properties: properties
      };

      // selling_plan is handled by Appstle's radio interceptor (syncAppstleRadio)
      // We still pass it in the body as a belt-and-suspenders fallback
      if (state.purchaseMode === 'sub') {
        var resolvedPlanId = resolveSellingPlanId();
        var planId = parseInt(resolvedPlanId, 10);
        if (!isNaN(planId) && planId > 0) {
          body.selling_plan = planId;
        }
      }

      // Loading state
      atcBtn.classList.add('is-loading');
      atcBtn.disabled = true;

      // Use the pristine fetch captured by the inline script in the section,
      // BEFORE Appstle's deferred script patched window.fetch.
      var doFetch = window.__hobOriginalFetch || window.fetch;

      // Ask Shopify to also re-render the cart drawer + icon bubble sections.
      // The response will include rendered HTML for these sections, which we
      // pass to cartDrawer.renderContents() to refresh the drawer (and clear
      // the is-empty state if the cart was just emptied).
      var cartDrawerEl = document.querySelector('cart-drawer');
      var sectionsToRender = (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function')
        ? cartDrawerEl.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-drawer', 'cart-icon-bubble'];
      body.sections = sectionsToRender.join(',');
      body.sections_url = window.location.pathname;

      doFetch(data.cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed: ' + res.status);
          return res.json();
        })
        .then(function (cartData) {
          atcBtn.classList.remove('is-loading');
          atcBtn.classList.add('is-success');

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cart: cartData });
          }

          // Re-render cart drawer with the freshly returned section HTML.
          // This refreshes the items list and clears is-empty when items are added
          // after the cart was emptied.
          if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
            cartDrawerEl.renderContents(cartData);
          }
          if (cartDrawerEl && typeof cartDrawerEl.open === 'function') cartDrawerEl.open();

          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));

          setTimeout(function () {
            atcBtn.classList.remove('is-success');
            atcBtn.disabled = false;
          }, 1600);
        })
        .catch(function (err) {
          console.error('[HOBConfigurator] Cart add error:', err);
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          if (atcTxt) {
            var prev = atcTxt.innerHTML;
            atcTxt.textContent = 'Error — please try again';
            setTimeout(function () { atcTxt.innerHTML = prev; }, 2500);
          }
        });
    }

    /* ── Wire ATC button ── */
    if (atcBtn) {
      atcBtn.addEventListener('click', handleATC);
    }

    /* ── Expose helpers on root element for debugging ── */
    root._hobCfg = {
      state: state,
      incQty: incQty,
      decQty: decQty,
      updateAll: updateAll
    };

    /* ── Initial render ── */
    initAccordions(root);
    initGallery(data, root);
    if (data.showVariantBadges !== false) initVariantBadges(data, root);
    updateAll();
  }

  /* ─────────────────────────────────────────────
     Bootstrap all configurators on the page
  ───────────────────────────────────────────── */
  function bootstrap() {
    var roots = qsa('[data-hob-cfg]');
    roots.forEach(function (root) {
      var sid = root.getAttribute('data-section-id');
      if (!sid) return;
      var dataScript = qs('#hob-cfg-data-' + sid);
      if (!dataScript) return;
      var data;
      try {
        data = JSON.parse(dataScript.textContent || dataScript.innerHTML);
      } catch (e) {
        console.error('[HOBConfigurator] Failed to parse data for section ' + sid, e);
        return;
      }
      initConfigurator(data);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /* ── Public API ── */
  window.HOBConfigurator = {
    init: bootstrap,
    initConfigurator: initConfigurator
  };

})();
